import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDynamicTranslateMany } from '@/hooks/useDynamicTranslate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_HOST = process.env.REACT_APP_BACKEND_URL;

// Default config; overridden by admin settings if present.
const DEFAULT = {
  background_type: 'image',
  background_url: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=1600',
  overlay_opacity: 0.65,
  headline_hr: 'Predaj svoju vijest!',
  subheadline_hr: 'Vidim, čujem, znam... Predaj vijest i budi promjena koju želiš vidjeti.',
  cta_label_hr: 'Predaj vijest',
  cta_url: '/predaj-vijest',
};

function resolveUrl(url) {
  if (!url) return url;
  if (url.startsWith('/api/')) return `${BACKEND_HOST}${url}`;
  if (url.startsWith('/uploads/')) return `${BACKEND_HOST}/api${url}`;
  return url;
}

export default function SubmitNewsSection() {
  const { t, i18n } = useTranslation();
  const [cfg, setCfg] = useState(DEFAULT);

  useEffect(() => {
    fetch(`${API}/public/homepage-settings`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.submit_section) setCfg({ ...DEFAULT, ...d.submit_section });
      }).catch(() => {});
  }, []);

  // Treat suspiciously-short admin values (stale test seed data like "T"/"S"/"C")
  // as missing so we fall back to the default copy.
  const pick = (val, fallback) => (typeof val === 'string' && val.trim().length >= 3 ? val : fallback);

  const lang = i18n.language;
  const headline = pick(cfg[`headline_${lang}`], pick(cfg.headline_hr, t('submit.title')));
  const subheadline = pick(cfg[`subheadline_${lang}`], pick(cfg.subheadline_hr, t('submit.subtitle')));
  const ctaLabel = pick(cfg[`cta_label_${lang}`], pick(cfg.cta_label_hr, t('submit.cta')));
  // Auto-translate the HR copy if admin hasn't provided a locale-specific
  // override — user expectation is that selecting any language translates
  // EVERYTHING on the page, including admin-authored copy.
  const dyn = useDynamicTranslateMany([headline, subheadline, ctaLabel]);
  const trHeadline = dyn[headline] || headline;
  const trSubheadline = dyn[subheadline] || subheadline;
  const trCtaLabel = dyn[ctaLabel] || ctaLabel;
  const bgUrl = resolveUrl(
    typeof cfg.background_url === 'string' && cfg.background_url.trim().length >= 10
      ? cfg.background_url
      : DEFAULT.background_url,
  );

  return (
    <section className="mt-8 relative rounded-xl overflow-hidden" data-testid="submit-news-section">
      {cfg.background_type === 'video' ? (
        <video src={bgUrl} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" data-testid="submit-section-video" />
      ) : (
        <img src={bgUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
      )}
      <div className="absolute inset-0 bg-black" style={{ opacity: cfg.overlay_opacity ?? 0.65 }} />
      <div className="relative p-8 md:p-16 text-center z-10">
        <h2 className="text-2xl md:text-4xl font-bold text-red-500 mb-3">{trHeadline}</h2>
        <p className="text-white/90 text-sm md:text-base max-w-xl mx-auto mb-6">{trSubheadline}</p>
        <Link to={cfg.cta_url || '/predaj-vijest'} className="inline-flex px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg text-sm" data-testid="submit-news-btn">
          {trCtaLabel}
        </Link>
      </div>
    </section>
  );
}
